import React from "react";

const PatternVisualizer = ({ patternType }) => {
  // 5x5 grid for visualization
  const grid = Array(5).fill(Array(5).fill(false));

  // Helper function to get highlighted cells for each pattern
  const getHighlightedCells = () => {
    switch (patternType) {
      // Existing patterns
      case "classic":
        return [
          // Rows or columns or diagonals (showing horizontal for example)
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ];
      case "fourCorners":
        return [
          [0, 0],
          [0, 4],
          [4, 0],
          [4, 4],
        ];
      case "letterX":
        return [
          [0, 0],
          [1, 1],
          [2, 2],
          [3, 3],
          [4, 4],
          [0, 4],
          [1, 3],
          [3, 1],
          [4, 0],
        ];
      case "letterL":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Bottom row
        ];
      case "letterT":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 2],
          [2, 2],
          [3, 2],
          [4, 2], // Middle column
        ];
      case "postage":
        return [
          [0, 0],
          [0, 1],
          [1, 0],
          [1, 1],
        ]; // Top-left 2x2
      case "letterH":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column
          [0, 4],
          [1, 4],
          [2, 4],
          [3, 4],
          [4, 4], // Right column
          [2, 1],
          [2, 2],
          [2, 3], // Middle row
        ];
      case "letterZ":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 3],
          [2, 2],
          [3, 1], // Diagonal
          [4, 0],
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Bottom row
        ];
      case "letterN":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column
          [0, 4],
          [1, 4],
          [2, 4],
          [3, 4],
          [4, 4], // Right column
          [1, 1],
          [2, 2],
          [3, 3], // Diagonal
        ];
      case "plus":
        return [
          [2, 0],
          [2, 1],
          [2, 2],
          [2, 3],
          [2, 4], // Middle row
          [0, 2],
          [1, 2],
          [3, 2],
          [4, 2], // Middle column
        ];
      case "diamond":
        return [
          [0, 2],
          [1, 1],
          [1, 3],
          [2, 0],
          [2, 4],
          [3, 1],
          [3, 3],
          [4, 2],
        ];
      case "centerSquare":
        return [
          [1, 1],
          [1, 2],
          [1, 3],
          [2, 1],
          [2, 2],
          [2, 3],
          [3, 1],
          [3, 2],
          [3, 3],
        ];
      case "corners2x2":
        return [
          [0, 0],
          [0, 1],
          [1, 0],
          [1, 1], // Top-left
          [0, 3],
          [0, 4],
          [1, 3],
          [1, 4], // Top-right
          [3, 0],
          [3, 1],
          [4, 0],
          [4, 1], // Bottom-left
          [3, 3],
          [3, 4],
          [4, 3],
          [4, 4], // Bottom-right
        ];
      case "frame":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column (except top)
          [1, 4],
          [2, 4],
          [3, 4],
          [4, 4], // Right column (except top)
          [4, 1],
          [4, 2],
          [4, 3], // Bottom row (except corners)
        ];
      case "checkmark":
        return [
          [4, 0],
          [3, 1],
          [2, 2],
          [1, 3],
          [0, 4],
          [1, 4],
          [2, 4],
        ];
      case "blackout":
        return Array.from({ length: 5 }, (_, i) =>
          Array.from({ length: 5 }, (_, j) => [i, j])
        ).flat();

      // NEW PATTERNS
      case "pyramid":
        return [
          [4, 0],
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Base
          [3, 1],
          [3, 2],
          [3, 3], // Second row
          [2, 1],
          [2, 2],
          [2, 3], // Third row
          [1, 2], // Top
        ];
      case "hourglass":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 1],
          [1, 3], // Second row edges
          [2, 2], // Middle
          [3, 1],
          [3, 3], // Fourth row edges
          [4, 0],
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Bottom row
        ];
      case "arrow":
        return [
          [4, 2], // Base
          [3, 1],
          [3, 2],
          [3, 3], // Second from bottom
          [2, 0],
          [2, 1],
          [2, 2],
          [2, 3],
          [2, 4], // Middle row
          [1, 2], // Second from top
          [0, 2], // Top
        ];
      case "cross":
        return [
          [0, 2],
          [1, 2],
          [2, 2],
          [3, 2],
          [4, 2], // Vertical
          [2, 0],
          [2, 1],
          [2, 3],
          [2, 4], // Horizontal
        ];
      case "doubleX":
        return [
          [0, 0],
          [1, 1],
          [2, 2],
          [3, 3],
          [4, 4], // Diagonal 1
          [0, 4],
          [1, 3],
          [3, 1],
          [4, 0], // Diagonal 2
          [0, 0],
          [0, 4],
          [4, 0],
          [4, 4], // Four corners
        ];
      case "letterU":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0], // Left column (except bottom)
          [4, 0],
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Bottom row
          [0, 4],
          [1, 4],
          [2, 4],
          [3, 4], // Right column (except bottom)
        ];
      case "letterO":
        return [
          [0, 1],
          [0, 2],
          [0, 3], // Top (except corners)
          [1, 0],
          [2, 0],
          [3, 0], // Left (except corners)
          [1, 4],
          [2, 4],
          [3, 4], // Right (except corners)
          [4, 1],
          [4, 2],
          [4, 3], // Bottom (except corners)
        ];
      case "letterY":
        return [
          [0, 0],
          [0, 4], // Top corners
          [1, 1],
          [1, 3], // Second row
          [2, 2],
          [3, 2],
          [4, 2], // Center vertical
        ];
      case "letterE":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [2, 1],
          [2, 2],
          [2, 3], // Middle row
          [4, 1],
          [4, 2],
          [4, 3],
          [4, 4], // Bottom row
        ];
      case "letterF":
        return [
          [0, 0],
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0], // Left column
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [2, 1],
          [2, 2],
          [2, 3], // Middle row
        ];
      case "heart":
        return [
          [0, 1],
          [0, 3], // Top corners
          [1, 0],
          [1, 2],
          [1, 4], // Second row
          [2, 0],
          [2, 1],
          [2, 2],
          [2, 3],
          [2, 4], // Third row
          [3, 1],
          [3, 2],
          [3, 3], // Fourth row
          [4, 2], // Bottom
        ];
      case "target":
        return [
          [1, 1],
          [1, 2],
          [1, 3],
          [2, 1],
          [2, 2],
          [2, 3],
          [3, 1],
          [3, 2],
          [3, 3],
        ];
      case "windmill":
        return [
          [0, 0],
          [0, 4],
          [4, 0],
          [4, 4], // Corners
          [1, 1],
          [1, 3],
          [3, 1],
          [3, 3], // Inner corners
          [2, 2], // Center
        ];
      case "kite":
        return [
          [0, 2], // Top
          [1, 1],
          [1, 2],
          [1, 3], // Second row
          [2, 0],
          [2, 1],
          [2, 2],
          [2, 3],
          [2, 4], // Middle row
          [3, 2], // Second-to-last row
          [4, 2], // Bottom
        ];
      case "stamp":
        return [
          [0, 0],
          [0, 1],
          [1, 0],
          [1, 1], // Top-left
          [0, 3],
          [0, 4],
          [1, 3],
          [1, 4], // Top-right
          [3, 0],
          [3, 1],
          [4, 0],
          [4, 1], // Bottom-left
          [3, 3],
          [3, 4],
          [4, 3],
          [4, 4], // Bottom-right
        ];
      case "dice":
        return [
          [0, 0],
          [0, 4], // Top corners
          [2, 2], // Middle
          [4, 0],
          [4, 4], // Bottom corners
        ];
      case "crown":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 0],
          [1, 2],
          [1, 4], // Second row peaks
          [2, 0],
          [2, 2],
          [2, 4], // Third row continuations
        ];
      case "star":
        return [
          [0, 2], // Top point
          [1, 1],
          [1, 3], // Upper arms
          [2, 0],
          [2, 1],
          [2, 2],
          [2, 3],
          [2, 4], // Middle row
          [3, 1],
          [3, 3], // Lower arms
          [4, 0],
          [4, 4], // Bottom points
        ];
      case "rainbowArch":
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4], // Top row
          [1, 0],
          [1, 4], // Second row edges
          [2, 0],
          [2, 4], // Third row edges
        ];
      case "cup":
        return [
          [0, 1],
          [0, 2],
          [0, 3], // Top
          [1, 0],
          [1, 4], // Upper sides
          [2, 0],
          [2, 4], // Middle sides
          [3, 0],
          [3, 1],
          [3, 2],
          [3, 3],
          [3, 4], // Cup base
          [4, 1],
          [4, 2],
          [4, 3], // Bottom
        ];
      default:
        return [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ]; // Default to horizontal line
    }
  };

  const highlightedCells = getHighlightedCells();

  // Check if a cell should be highlighted
  const isCellHighlighted = (rowIndex, colIndex) => {
    return highlightedCells.some(
      (cell) => cell[0] === rowIndex && cell[1] === colIndex
    );
  };

  return (
    <div className="pattern-visualizer w-16 h-16 md:w-20 md:h-20 relative">
      <div className="grid grid-cols-5 gap-0.5 h-full w-full">
        {Array(5)
          .fill()
          .map((_, rowIndex) =>
            Array(5)
              .fill()
              .map((_, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={`rounded-sm border border-purple-200 flex items-center justify-center ${
                    isCellHighlighted(rowIndex, colIndex)
                      ? "bg-purple-600"
                      : "bg-purple-100"
                  }`}
                >
                  {rowIndex === 2 &&
                    colIndex === 2 &&
                    patternType !== "blackout" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-300"></div>
                    )}
                </div>
              ))
          )}
      </div>
    </div>
  );
};

export default PatternVisualizer;
